import { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import {
  Box,
  Button as MuiButton,
  IconButton,
  InputLabel,
  Stack,
  Theme,
} from '@mui/material';

import { IcDeleteTrash, IcPlus } from '@/assets/images';
import { RenderFormField } from '@/components';
import { Button } from '@/components/TailwindComponents';
import { IFormSelectItem, IMasterVehicleLicensePlate } from '@/models';
import { colors } from '@/theme/variables';

import { vehicleYears } from '../data';

interface MasterVehicleLicensePlatesFieldProps {
  licensePlateStateOptions: IFormSelectItem[];
  licensePlateTypeOptions: IFormSelectItem[];
}

type LicensePlatesFormValues = {
  licensePlates?: IMasterVehicleLicensePlate[];
};

const EMPTY_LICENSE_PLATE: IMasterVehicleLicensePlate = {
  number: '',
  state: '',
  year: '',
  type: '',
  isActive: true,
};

const plateFieldSx = (theme: Theme) => ({
  mb: 0,
  '& .MuiInputLabel-root': {
    marginBottom: '6px',
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: '1.25rem',
    ...(theme.palette.mode === 'light' && {
      color: colors.grey[100],
    }),
  },
  '& .MuiInputBase-root': {
    minHeight: 40,
    height: 40,
    padding: '8px 12px',
    borderRadius: '8px',
    borderColor: '#D8E0E6',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'transparent!important'
        : `${colors.white}!important`,
    ...(theme.palette.mode === 'light' && {
      color: `${colors.grey[100]}!important`,
    }),
  },
  '& .MuiInputBase-root input': {
    ...(theme.palette.mode === 'light' && {
      color: `${colors.grey[100]}!important`,
    }),
  },
  '& .MuiAutocomplete-root .MuiInputBase-root': {
    minHeight: 40,
    height: 40,
    padding: '0 40px 0 12px',
  },
  '& .MuiAutocomplete-endAdornment': {
    right: 10,
  },
  '& .MuiAutocomplete-popupIndicator': {
    color: colors.grey[70],
  },
  '& .MuiAutocomplete-popupIndicator:hover': {
    backgroundColor: 'transparent',
  },
});

const hiddenActionLabelSx = {
  visibility: 'hidden',
  display: { xs: 'none', md: 'block' },
  marginBottom: '6px',
  fontSize: '0.875rem',
  fontWeight: 600,
  lineHeight: '1.25rem',
};

export const MasterVehicleLicensePlatesField = ({
  licensePlateStateOptions,
  licensePlateTypeOptions,
}: MasterVehicleLicensePlatesFieldProps) => {
  const { clearErrors, control, watch } =
    useFormContext<LicensePlatesFormValues>();
  const { fields, append, replace } = useFieldArray({
    control,
    name: 'licensePlates',
  });

  const licensePlates =
    (watch('licensePlates') as IMasterVehicleLicensePlate[] | undefined) || [];

  const hasAnyPlateRowValue = (plate?: IMasterVehicleLicensePlate) => {
    if (!plate) return false;
    return Boolean(plate.number || plate.state || plate.year || plate.type);
  };

  const validatePlateNumber = (index: number, value?: string) => {
    const plate = licensePlates[index];
    const hasAnyValue = hasAnyPlateRowValue({
      ...plate,
      number: value ?? plate?.number,
    });

    if (hasAnyValue && !String(value || '').trim()) {
      return 'License plate number is required when license plate details are provided.';
    }

    return true;
  };

  const validatePlateState = (index: number, value?: string) => {
    const plate = licensePlates[index];
    const hasAnyValue = hasAnyPlateRowValue({
      ...plate,
      state: value ?? plate?.state,
    });

    if (hasAnyValue && !String(value || '').trim()) {
      return 'License plate state is required when license plate details are provided.';
    }

    return true;
  };

  useEffect(() => {
    if (licensePlates.length === 0) {
      replace([{ ...EMPTY_LICENSE_PLATE }]);
      return;
    }

    const activeIndexes = licensePlates.reduce<number[]>(
      (acc, plate, index) => {
        if (plate?.isActive) {
          acc.push(index);
        }
        return acc;
      },
      [],
    );

    if (licensePlates.length === 1 && !licensePlates[0]?.isActive) {
      replace([{ ...licensePlates[0], isActive: true }]);
      return;
    }

    if (activeIndexes.length === 1) {
      return;
    }

    const nextActiveIndex = activeIndexes[0] ?? 0;
    replace(
      licensePlates.map((plate, index) => ({
        ...plate,
        isActive: index === nextActiveIndex,
      })),
    );
  }, [licensePlates, replace]);

  const handleAddPlate = () => {
    append({
      ...EMPTY_LICENSE_PLATE,
      isActive: licensePlates.length === 0,
    });
  };

  const handleSetActive = (index: number) => {
    replace(
      licensePlates.map((plate, plateIndex) => ({
        ...plate,
        isActive: plateIndex === index,
      })),
    );
  };

  const handleRemove = (index: number) => {
    clearErrors('licensePlates');

    const updated = licensePlates.filter(
      (_, plateIndex) => plateIndex !== index,
    );

    if (updated.length === 0) {
      replace([{ ...EMPTY_LICENSE_PLATE }]);
      return;
    }

    const hasActive = updated.some((plate) => plate.isActive);
    replace(
      updated.map((plate, plateIndex) => ({
        ...plate,
        isActive:
          updated.length === 1
            ? true
            : hasActive
            ? Boolean(plate.isActive)
            : plateIndex === 0,
      })),
    );
  };

  return (
    <Stack spacing={1.5} width="100%">
      {fields.map((field, index) => {
        const plate = licensePlates[index];
        const isActive = Boolean(plate?.isActive);

        return (
          <Box
            key={field.id}
            sx={{
              display: 'grid',
              rowGap: 1.25,
              columnGap: 1.75,
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(4, minmax(0, 1fr)) 34px 148px',
              },
              alignItems: 'start',
            }}
          >
            <RenderFormField
              name={`licensePlates.${index}.number`}
              label="License plate number"
              transformValue={(value: string) => value.toUpperCase()}
              sx={plateFieldSx}
              rules={{
                validate: (value: string) => validatePlateNumber(index, value),
              }}
            />
            <RenderFormField
              name={`licensePlates.${index}.state`}
              label="License plate state"
              type="autocomplete"
              multiple={false}
              items={licensePlateStateOptions}
              sx={plateFieldSx}
              rules={{
                validate: (value: string) => validatePlateState(index, value),
              }}
            />
            <RenderFormField
              name={`licensePlates.${index}.year`}
              label="License plate year"
              type="autocomplete"
              multiple={false}
              items={vehicleYears}
              sx={plateFieldSx}
              rules={{
                validate: (value: string | null) => {
                  if (!value) return true;
                  const year = parseInt(value, 10);
                  if (year < 1900) return 'Year must be after 1900';
                  if (year > new Date().getFullYear())
                    return 'Year cannot be in the future';
                  return true;
                },
              }}
            />
            <RenderFormField
              name={`licensePlates.${index}.type`}
              label="License plate type"
              type="autocomplete"
              multiple={false}
              items={licensePlateTypeOptions}
              sx={plateFieldSx}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: { xs: 'flex-start', md: 'center' },
              }}
            >
              <InputLabel sx={hiddenActionLabelSx}>Delete</InputLabel>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 40,
                }}
              >
                <IconButton
                  aria-label={`Delete license plate ${index + 1}`}
                  onClick={() => handleRemove(index)}
                  sx={{
                    color: colors.error.main,
                    height: 24,
                    width: 24,
                    minWidth: 24,
                    minHeight: 24,
                    padding: 0,
                    borderRadius: '6px',
                  }}
                >
                  <IcDeleteTrash />
                </IconButton>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: { xs: 'flex-start', md: 'center' },
              }}
            >
              <InputLabel sx={hiddenActionLabelSx}>Action</InputLabel>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: { xs: 'flex-start', md: 'center' },
                  height: 40,
                }}
              >
                <MuiButton
                  variant="contained"
                  color={isActive ? 'inherit' : 'error'}
                  disabled={isActive}
                  onClick={() => handleSetActive(index)}
                  sx={{
                    minWidth: { xs: '100%', md: 136 },
                    height: 36,
                    px: 1.75,
                    borderRadius: '8px',
                    boxShadow: 'none',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    '&:hover': {
                      boxShadow: 'none',
                    },
                    ...(isActive && {
                      '&.Mui-disabled': {
                        opacity: 1,
                        backgroundColor: '#EDF1F4',
                        color: '#AEB7C0',
                      },
                    }),
                    ...(!isActive && {
                      backgroundColor: colors.error.main,
                      '&:hover': {
                        backgroundColor: colors.error.dark,
                      },
                    }),
                  }}
                >
                  {isActive ? 'Active plate' : 'Set as active'}
                </MuiButton>
              </Box>
            </Box>
          </Box>
        );
      })}

      <Box sx={{ pt: 0.5 }}>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="h-10 w-fit rounded-lg bg-[#F3F6F8] px-3.5 text-sm font-semibold text-[#1F3B57] shadow-none hover:bg-[#E7EDF1] active:bg-[#DCE4EA]"
          onClick={handleAddPlate}
        >
          <IcPlus />
          Add plate
        </Button>
      </Box>
    </Stack>
  );
};
